import cloneDeep from 'clone-deep';

import Axis from 'util/table/Axis';
import Matrix from 'util/table/Matrix';
import Table from 'util/table/Table';
import TableFactory from 'util/TableFactory';

/**
 * @typedef {Object} LoadedMap - a map as the editor's windows read it: the payload of GET /projects/{id}/maps/{name} (API 1.2) built into tables
 * @property {string} id - the map's name
 * @property {string} name
 * @property {string} description
 * @property {string} nameDescription - the definition's description, or the name when it has none
 * @property {number} latestVersion
 * @property {string} type - "3d", "2d" or "single"
 * @property {string} mapSize
 * @property {number} calibrationStartAddress - an absolute file offset
 * @property {number} calibrationEndAddress
 * @property {boolean} favourited
 * @property {boolean} swModified - the upload and the working file differ on this map or its axes
 * @property {boolean} edited - the upload differs from the release original, or swModified
 * @property {Object} raw - the payload as received, kept so that a window can be built again from the last good state
 * @property {boolean} hasOriginal - the release original could be read, so "Original" can be shown
 * @property {?Table} original - null when hasOriginal is false
 * @property {Table} uploaded
 * @property {Table} modified - the working file's section, or the upload's when the project has no working file
 * @property {boolean} notSaved - the values on screen are not the ones the API holds: any part is unsaved, or the state of the map is unknown (the badge
 *   reads it; it is derived from the next two, and mapActions writes all three together)
 * @property {{data: boolean, X: boolean, Y: boolean}} unsavedParts - the parts of the map whose values on screen are not the ones the API holds: its data,
 *   and its X and Y axes, which are saved apart and so are marked apart (Task 9)
 * @property {boolean} [stateUnknown] - set by mapActions when a request of the map got no answer and the read after it failed: what the API holds is not known
 */

/**
 * The uploaded section of a map has no values: its address lies outside the uploaded file, so there is nothing to show or to edit. The map does not
 * open, and the user is told so (COPY.mapUnreadable).
 */
export class UnreadableMapError extends Error {
    constructor(mapName) {
        super(`The uploaded file holds no values for map "${mapName}"`);
        this.name = 'UnreadableMapError';
        this.mapName = mapName;
    }
}

// A section is the data axis of a map and the X and Y axes the reader found for it: a value map has neither.
const axesOf = section => [section.dataAxis, section.xAxis, section.yAxis].filter(Boolean);

// The API sends `values: null` for a section whose address lies outside the file that section is read from, and for the axes of that section. A
// section that is null as a whole (the contract has none, but a payload can lack one) has no values either.
const isReadable = section => section != null && axesOf(section).every(axis => axis.values !== null);

// A null unit or name would show as the text "null" in the sheet's headers, the chart titles and the 3D hover text (F2 section 4f). The API sends
// an axis's name equal to its unit, so a missing name falls back to the unit.
const withDefaults = axis => ({...axis, units: axis.units ?? '', name: axis.name ?? axis.units ?? ''});

/**
 * A table of one section. The section is cloned first, so that no table shares an object with another table or with the payload the map keeps:
 * the editor changes the modified table in place (Window.js, Viewer.js), and a table shared with the uploaded one would lose the highlight of
 * what changed and make "Reset changes" send the edits back (F2 section 4d).
 */
const tableOf = (section, type) => {
    const {dataAxis, xAxis, yAxis} = cloneDeep(section);

    return TableFactory.createTable({
        type,
        dataAxis: withDefaults(dataAxis),
        xAxis: xAxis ? withDefaults(xAxis) : undefined,
        yAxis: yAxis ? withDefaults(yAxis) : undefined,
    });
};

/**
 * Builds the map a window shows from the payload of the single-map read.
 * @param {Object} raw - the payload as the API sent it. It is kept as `raw` and never changed.
 * @returns {LoadedMap}
 * @throws {UnreadableMapError} when any axis of the uploaded section has no values
 */
export const buildMap = raw => {
    const {name, type} = raw;

    // Only the uploaded section is checked. Modified is read at the same address of a working file that the API holds to the size of the upload (a
    // mismatch is a 500), so it has values wherever the upload has.
    if (!isReadable(raw.uploaded)) throw new UnreadableMapError(name);

    // The original is a different file, read at offset 0, and it can fail to have values while the upload has them (F2 section 4b). The map then
    // opens without it, and its window does not offer "Original".
    const hasOriginal = isReadable(raw.original);

    return {
        id: raw.id,
        name,
        description: raw.description,
        nameDescription: raw.nameDescription ?? name,
        latestVersion: raw.latestVersion,
        type,
        mapSize: raw.mapSize,
        calibrationStartAddress: raw.calibrationStartAddress,
        calibrationEndAddress: raw.calibrationEndAddress,
        favourited: raw.favourited,
        swModified: raw.swModified,
        edited: raw.edited,
        raw,
        hasOriginal,
        original: hasOriginal ? tableOf(raw.original, type) : null,
        uploaded: tableOf(raw.uploaded, type),
        // The API sends modified: null while the project has no working file, and the legacy API filled it with the upload (F2 section 4d).
        modified: tableOf(raw.modified ?? raw.uploaded, type),
        notSaved: false,
        unsavedParts: {data: false, X: false, Y: false},
    };
};

/**
 * The letter that names an axis on the wire (API 1.3: `X` or `Y`), for the name the state gives it by position (a window's `axisEditingAxisName`).
 * @param {'xAxis'|'yAxis'} axisEditingAxisName
 * @returns {'X'|'Y'}
 */
export const axisLetter = axisEditingAxisName => ({xAxis: 'X', yAxis: 'Y'})[axisEditingAxisName];

/**
 * The table with the named axes put back to the uploaded section's, for the reset: a new table, with new axes for those, so that whatever compares the
 * table it had with this one by value (Visual does) sees the difference; the axes that are not named are the ones `table` has. The matrices are clones of
 * the uploaded ones, so that the modified and uploaded tables share none, as buildMap keeps them (F2 section 4d).
 * @param {Table} table - the modified table of a loaded map
 * @param {Table} uploaded - the uploaded table of the same map
 * @param {Array<'dataAxis'|'xAxis'|'yAxis'>} axisKeys - the axes to put back; a key that the table has no axis for is left
 * @returns {Table}
 */
export const tableWithUploadedAxes = (table, uploaded, axisKeys) => {
    const axes = {};
    axisKeys.forEach(key => {
        if (table[key] && uploaded[key]) axes[key] = new Axis({...table[key], matrix: new Matrix({values: cloneDeep(uploaded[key].matrix.values), precision: uploaded[key].matrix.precision})});
    });

    return new Table({...table, ...axes});
};
